export type PublicEvent = {
  id: string;
  source: string;
  event_kind: string;
  event_name: string;
  venue: string | null;
  prefecture: string | null;
  event_date: string;
  time_start: string | null;
  time_end: string | null;
  door_time: string | null;
  curtain_time: string | null;
  door_time_2: string | null;
  curtain_time_2: string | null;
  door_time_3: string | null;
  curtain_time_3: string | null;
  performers: string[];
  description: string | null;
  detail_url: string | null;
  reserve_url: string | null;
  memo: string | null;
  fan_group_name?: string | null;
  fan_artist_name?: string | null;
  source_title?: string | null;
  fan_submitter_key_hash?: string | null;
  performance_count?: number | null;
  is_karin: boolean;
  karin_subtype: string | null;
  /** 宮本佳林予定の表サイト表示アイコン。未設定なら従来の顔スタンプ */
  karin_icon?: string | null;
  group_code: string | null;
  reception_start: string | null;
  reception_end: string | null;
  reception_period_text?: string | null;
  /** 本番日程・開催枠（DB / 公開 JSON 同期後に入る） */
  performance_text?: string | null;
};

/** 公開 JSON が古い／欠損のときでも落ちないよう正規化 */
export function publicPerformersList(e: Pick<PublicEvent, "performers">): string[] {
  const p = e.performers as unknown;
  return Array.isArray(p) ? p.map(String).filter(Boolean) : [];
}

export type MonthBundle = {
  month: string;
  generatedAt: string;
  events: PublicEvent[];
  byDay: Record<string, { karin: PublicEvent[]; others: PublicEvent[] }>;
};

export type DayBundle = {
  date: string;
  generatedAt: string;
  events: PublicEvent[];
};

export const GROUP_BADGE: Record<string, { short: string; color: string; label: string; fg?: string }> = {
  mm: { short: "娘", color: "#e53935", label: "モーニング娘。" },
  angerme: { short: "ア", color: "#1e88e5", label: "アンジュルム" },
  juice: { short: "ジ", color: "#8e24aa", label: "Juice=Juice" },
  tsuki: { short: "椿", color: "#ec407a", label: "つばきファクトリー" },
  beyooooonds: { short: "ビ", color: "#43a047", label: "BEYOOOOONDS" },
  ocha: { short: "茶", color: "#fb8c00", label: "OCHA NORMA" },
  rosy: { short: "ロ", color: "#fdd835", label: "ロージークロニクル", fg: "#4e342e" },
  other: { short: "他", color: "#78909c", label: "その他" },
};

export type PublicEventBadge = { short: string; color: string; label: string; fg?: string };

const MLINE_ARTIST_RULES: { keys: string[]; badge: PublicEventBadge }[] = [
  { keys: ["佐々木莉佳子"], badge: { short: "莉", color: "#fbc02d", label: "佐々木莉佳子", fg: "#3e2723" } },
  { keys: ["譜久村聖"], badge: { short: "譜", color: "#c2185b", label: "譜久村聖" } },
  { keys: ["稲場愛香"], badge: { short: "稲", color: "#ad1457", label: "稲場愛香" } },
  { keys: ["竹内朱莉"], badge: { short: "竹", color: "#1565c0", label: "竹内朱莉" } },
  { keys: ["石田亜佑美"], badge: { short: "石", color: "#1976d2", label: "石田亜佑美" } },
  { keys: ["佐藤優樹"], badge: { short: "樹", color: "#00897b", label: "佐藤優樹" } },
  { keys: ["宮崎由加"], badge: { short: "宮", color: "#f48fb1", label: "宮崎由加", fg: "#4a148c" } },
  { keys: ["植村あかり"], badge: { short: "植", color: "#2e7d32", label: "植村あかり" } },
  { keys: ["森戸知沙希"], badge: { short: "森", color: "#ef6c00", label: "森戸知沙希" } },
  { keys: ["小関舞"], badge: { short: "小", color: "#1e88e5", label: "小関舞" } },
  { keys: ["浅倉樹々"], badge: { short: "浅", color: "#ec407a", label: "浅倉樹々" } },
];

const HP_GROUP_HINTS: { re: RegExp; code: keyof typeof GROUP_BADGE }[] = [
  { re: /モーニング娘|モーニング\s*娘/, code: "mm" },
  { re: /アンジュルム/, code: "angerme" },
  { re: /Juice\s*=\s*Juice|ジュース/, code: "juice" },
  { re: /つばきファクトリー|つばき/, code: "tsuki" },
  { re: /BEYOO|ビヨンド/i, code: "beyooooonds" },
  { re: /OCHA\s*NORMA|オチャノーマ/i, code: "ocha" },
  { re: /ロージー|ロージークロニクル/i, code: "rosy" },
];

function badgeFromGroupCode(code: string | null | undefined): PublicEventBadge {
  if (code && GROUP_BADGE[code]) return GROUP_BADGE[code] as PublicEventBadge;
  return GROUP_BADGE.other;
}

function inferMlineBadgeFromText(haystack: string): PublicEventBadge | null {
  if (!haystack.trim()) return null;
  for (const { keys, badge } of MLINE_ARTIST_RULES) {
    for (const k of keys) {
      if (haystack.includes(k)) return badge;
    }
  }
  return null;
}

function inferHpBadgeFromText(haystack: string): PublicEventBadge | null {
  for (const { re, code } of HP_GROUP_HINTS) {
    if (re.test(haystack)) return GROUP_BADGE[code] as PublicEventBadge;
  }
  return null;
}

/** カレンダー・一覧・日別の色付き丸ラベル */
export function getPublicEventBadge(e: PublicEvent): PublicEventBadge {
  const src = e.source ?? "";
  const perfArr = publicPerformersList(e);
  const perfBlob = perfArr.length ? perfArr.join("・") : "";
  const hay = [e.event_name, perfBlob, e.fan_group_name ?? "", e.fan_artist_name ?? "", e.source_title ?? "", e.description ?? ""].filter(Boolean).join(" ");

  if (src === "fan_post" || e.fan_group_name || e.fan_artist_name) {
    const m = inferMlineBadgeFromText(perfBlob) ?? inferMlineBadgeFromText(hay);
    if (m) return m;
    const hp = inferHpBadgeFromText(hay);
    if (hp) return hp;
    return badgeFromGroupCode(e.group_code);
  }

  if (src === "helloproject") {
    const fromCode = e.group_code ? badgeFromGroupCode(e.group_code) : null;
    if (fromCode && fromCode !== GROUP_BADGE.other) return fromCode;
    const hinted = inferHpBadgeFromText(hay);
    if (hinted) return hinted;
    return fromCode ?? GROUP_BADGE.other;
  }

  if (src === "jp_room" || src === "mline") {
    const m = inferMlineBadgeFromText(perfBlob) ?? inferMlineBadgeFromText(hay);
    if (m) return m;
    return { short: "M", color: "#9575cd", label: "M-line / J.P ROOM" };
  }

  return badgeFromGroupCode(e.group_code);
}

export const KARIN_SUB_LABEL: Record<string, string> = {
  mini_live: "ミニライブ＆一言お話し会",
  withlive: "オンラインお話し会(WithLIVE)",
  limista: "オンラインサイン会(リミスタ)",
  live: "ライブ",
  m_line: "M-line",
  other: "その他",
};

export const KARIN_ICON_OPTIONS = [
  { value: "face_live", emoji: "顔", label: "顔アイコン（現場）" },
  { value: "face_online", emoji: "顔", label: "顔アイコン（オンライン）" },
  { value: "🎤", emoji: "🎤", label: "ライブ・ステージ" },
  { value: "🎶", emoji: "🎶", label: "音楽" },
  { value: "💿", emoji: "💿", label: "リリース" },
  { value: "🤝", emoji: "🤝", label: "お話し会・特典会" },
  { value: "✍️", emoji: "✍️", label: "サイン・書きもの" },
  { value: "📻", emoji: "📻", label: "ラジオ" },
  { value: "📺", emoji: "📺", label: "テレビ" },
  { value: "📱", emoji: "📱", label: "配信・SNS" },
  { value: "🎬", emoji: "🎬", label: "映画・映像" },
  { value: "📚", emoji: "📚", label: "雑誌・本" },
  { value: "🎫", emoji: "🎫", label: "チケット・申込" },
] as const;

const KARIN_ICON_VALUE_SET = new Set<string>(KARIN_ICON_OPTIONS.map((o) => o.value).filter(Boolean));

export function normalizeKarinIcon(value: unknown): string | null {
  const v = value == null ? "" : String(value).trim();
  return KARIN_ICON_VALUE_SET.has(v) ? v : null;
}

/** 会場に行かない予定（配信・メディア） — 表でラベル・カレンダー印を分ける */
export const REMOTE_EVENT_KINDS = new Set(["stream", "radio", "tv", "magazine", "web"]);

export function isRemoteEventKind(kind: string): boolean {
  return REMOTE_EVENT_KINDS.has(kind);
}

/** カレンダー「オンライン」印：配信・メディア＋WithLIVE・リミスタ */
export const ONLINE_KARIN_SUBTYPES = new Set(["withlive", "limista"]);

export function isOnlineKarinStampEvent(e: Pick<PublicEvent, "event_kind" | "karin_subtype">): boolean {
  if (isRemoteEventKind(e.event_kind)) return true;
  const sub = String(e.karin_subtype ?? "").trim();
  return ONLINE_KARIN_SUBTYPES.has(sub);
}

export function remoteKindLabel(kind: string): string | null {
  const map: Record<string, string> = {
    stream: "配信・中継",
    radio: "ラジオ",
    tv: "テレビ",
    magazine: "雑誌・書籍",
    web: "WEB",
  };
  return map[kind] ?? null;
}

/** 表サイト用：日本時間の今日 YYYY-MM-DD */
export function jstTodayYmd(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}

/** 表サイト用：日本時間の当月 YYYY-MM */
export function jstCurrentYearMonth(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(now);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  return `${y}-${m}`;
}

export function weekdayJp(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const map = ["日", "月", "火", "水", "木", "金", "土"];
  return map[dt.getUTCDay()] ?? "";
}

export function formatTimeRange(e: PublicEvent): string {
  const parts: string[] = [];
  if (e.time_start) parts.push(e.time_start);
  if (e.time_end) parts.push(e.time_end);
  return parts.join("〜") || "—";
}

/** 一覧用：本番メモを1行に短縮（改行・連続空白を畳む） */
export function previewPerformanceOneLine(text: string | null | undefined, maxLen = 160): string | null {
  const t = String(text ?? "")
    .trim()
    .replace(/\s+/g, " ");
  if (!t) return null;
  return t.length > maxLen ? `${t.slice(0, maxLen)}…` : t;
}

/** Hello! Project 詳細展開時の memo `hp_slots=n` から当日公演数を取る */
export function parseHpSlotsFromMemo(memo: string | null | undefined): number | null {
  const m = String(memo ?? "").match(/hp_slots=(\d+)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** 第1〜第3公演の開場・開演（入っている枠だけ） */
export function formatDoorCurtainSlots(e: PublicEvent): string {
  const marks = ["①", "②", "③"];
  const pairs: { door: string | null; cur: string | null }[] = [
    { door: e.door_time, cur: e.curtain_time },
    { door: e.door_time_2, cur: e.curtain_time_2 },
    { door: e.door_time_3, cur: e.curtain_time_3 },
  ];
  const out: string[] = [];
  for (let i = 0; i < pairs.length; i++) {
    const d = String(pairs[i]!.door ?? "").trim();
    const c = String(pairs[i]!.cur ?? "").trim();
    if (!d && !c) continue;
    out.push(`${marks[i]!}開場${d || "—"}／開演${c || "—"}`);
  }
  return out.join("　");
}
